import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { translateBatch } from "@/services/video/googleTranslate"
import { type SubtitleResult, VideoApiError, videoService } from "@/services/video/videoApi"

export function useVideoTranslation(id: string, subtitles: SubtitleResult | undefined, enabled: boolean) {
  const queryClient = useQueryClient()
  const [running, setRunning] = useState(false)
  const [error, setError] = useState("")
  const active = useRef<{ stop: boolean; controller: AbortController } | null>(null)
  const mounted = useRef(false)
  const autoStarted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (active.current) {
        active.current.stop = true
        active.current.controller.abort()
      }
    }
  }, [])

  async function start(initial?: SubtitleResult) {
    if (active.current || !mounted.current) return
    const run = { stop: false, controller: new AbortController() }
    active.current = run
    setRunning(true)
    setError("")
    try {
      let current = initial || queryClient.getQueryData<SubtitleResult>(["video-subtitles", id])
      while (current && current.remaining > 0 && !run.stop && mounted.current) {
        const batch = current.document.segments.filter((segment) => !segment.translation).slice(0, 5)
        const translated = await translateBatch(batch, current.document.sourceLanguage, {
          signal: run.controller.signal,
          shouldStop: () => run.stop || !mounted.current,
        })
        if (translated.translations.length) {
          current = await videoService.saveTranslations(id, current.revision, translated.translations)
          queryClient.setQueryData(["video-subtitles", id], current)
          void queryClient.invalidateQueries({ queryKey: ["video-detail", id] })
        }
        if (translated.error) throw translated.error
        if (!translated.translations.length) break
      }
    } catch (cause) {
      if (mounted.current) {
        setError(cause instanceof Error ? cause.message : "Dịch không thành công.")
        if (cause instanceof VideoApiError && cause.status === 409)
          void queryClient.invalidateQueries({ queryKey: ["video-subtitles", id] })
      }
    } finally {
      active.current = null
      if (mounted.current) setRunning(false)
    }
  }

  useEffect(() => {
    if (!enabled || !subtitles?.remaining || autoStarted.current) return
    let disposed = false
    queueMicrotask(() => {
      if (disposed) return
      autoStarted.current = true
      void start()
    })
    return () => {
      disposed = true
    }
  }, [enabled, subtitles])

  return {
    running,
    error,
    start,
    stop: () => {
      if (active.current) active.current.stop = true
    },
  }
}
